/**
 * Memory Lane Roadmap — the dementia caregiving journey
 * =========================================================
 *
 * NINE journey stops, framed in plain caregiver language (not clinical
 * staging). The stages are the JOURNEY — first concerns, getting
 * answers, diagnosis and next steps, daily care changes, care
 * coordination, transitions, advanced care needs, hospice / comfort
 * care, grief and family support.
 *
 * FAST is a clinical staging system built around Alzheimer's disease.
 * Not every dementia follows FAST cleanly (vascular, Lewy body, FTD,
 * mixed). We therefore treat FAST as SECONDARY, opt-in metadata that
 * only surfaces when the caregiver's patient profile indicates
 * Alzheimer's or suspected Alzheimer's. For every other type — and
 * for caregivers who haven't yet been told a type — the roadmap shows
 * only the plain-language journey labels.
 *
 * Each stop carries the eight caregiver-facing sections the product
 * spec requires: meaning, concerns, watch_for, next_steps,
 * doctor_questions, documents, services, lane_help.
 *
 * A separate `service_shifts` list per stop tells the Roadmap page
 * which services PICK UP here and which START TO FADE — the "on/off
 * ramp" metaphor. Caregiver wellness and community support are
 * modelled as REST STOPS ("always open") rather than stops on the
 * road, because they never leave.
 */

// ---- Service catalog --------------------------------------------------------

export const SERVICES = {
    primary_care: { name: "Primary Care", kind: "medical", tone: "teal" },
    neurology: { name: "Neurology", kind: "medical", tone: "teal" },
    geriatrician: { name: "Geriatrician", kind: "medical", tone: "teal" },
    memory_clinic: { name: "Memory / Cognitive Clinic", kind: "medical", tone: "teal" },
    guide_program: { name: "Medicare GUIDE Program", kind: "program", tone: "sage" },
    home_health: { name: "Home Health Aide", kind: "care", tone: "coral" },
    adult_day: { name: "Adult Day Program", kind: "care", tone: "coral" },
    respite: { name: "Respite Care", kind: "care", tone: "coral" },
    memory_care: { name: "Memory Care Community", kind: "care", tone: "coral" },
    elder_law: { name: "Elder Law Attorney", kind: "legal", tone: "lavender" },
    financial_planner: { name: "Financial Planner", kind: "legal", tone: "lavender" },
    social_worker: { name: "Hospital / Aging Social Worker", kind: "care", tone: "sand" },
    support_group: { name: "Caregiver Support Group", kind: "support", tone: "sand" },
    alz_helpline: { name: "Alzheimer's 24/7 Helpline", kind: "support", tone: "sand" },
    palliative: { name: "Palliative Care", kind: "medical", tone: "teal" },
    hospice: { name: "Hospice", kind: "care", tone: "coral" },
    grief_counselor: { name: "Grief Counselor", kind: "support", tone: "sand" },
    faith_community: { name: "Faith / Spiritual Community", kind: "support", tone: "sand" },
    pharmacist: { name: "Pharmacist (medication review)", kind: "medical", tone: "teal" },
    ot: { name: "Occupational Therapist", kind: "medical", tone: "teal" },
    driver_eval: { name: "Driving Evaluation Program", kind: "medical", tone: "teal" },
    speech_therapy: { name: "Speech / Swallow Therapy", kind: "medical", tone: "teal" },
    community_meals: { name: "Meals on Wheels / community food", kind: "support", tone: "sand" },
    transport: { name: "Senior Transport / Paratransit", kind: "support", tone: "sand" },
};

const s = (k, note = "") => ({ ...SERVICES[k], note, key: k });

// ---- Rest stops (always open) ----------------------------------------------
//
// These aren't stages — they're standing offers. Rendered as a
// persistent strip in the Roadmap header so the caregiver knows they
// don't have to be at a particular stage to use them.

export const REST_STOPS = [
    { key: "caregiver_wellness", name: "Your own wellness", detail: "Sleep, stress, grief. Check in on you." },
    { key: "community_support", name: "Community support", detail: "Support groups, helplines, faith communities." },
    { key: "lane_chat", name: "Lane", detail: "Ask anything, anytime. She remembers your story." },
];

// ---- Journey stops ---------------------------------------------------------

export const ROADMAP_STAGES = [
    {
        key: "first_concerns",
        color: "#1E9E63",
        color_soft: "#D7F3E5",
        title: "First concerns",
        tagline: "Something feels different. You're paying attention.",
        accent: "bg-stage_early text-stage_early-dark",
        accent_dark: "text-stage_early-dark",
        icon: "eye",
        // FAST maps here only when the caregiver has told us it's
        // Alzheimer's or suspected Alzheimer's. Everyone else sees
        // just the plain-language label above.
        fast_label: "Possible FAST 1–2 range",
        matches: (p) => p?.predx === true || p?.estimated_stage === 0 || p?.estimated_stage === 1,
        meaning: "Memory slips, repeated questions, missed bills, confusion, driving concerns. Nothing has been named yet — but you're noticing. This is the very beginning of the road. Being here early is a gift to your loved one.",
        concerns: [
            "\"Is this normal aging or something more?\"",
            "\"Am I overreacting? Do I bring this up with them?\"",
            "\"What if they get angry when I mention it?\"",
            "\"Where do I even start looking for answers?\"",
        ],
        watch_for: [
            "Repeating the same question inside one conversation",
            "Missed bills, expired food, unopened mail piling up",
            "Getting lost on a familiar route",
            "Personality shifts — new suspicion, apathy, or irritability",
            "Falling for scam phone calls or emails",
        ],
        next_steps: [
            "Write down 3–5 specific examples with dates. Patterns beat impressions at the doctor's office.",
            "Book a baseline visit with their Primary Care provider. Frame it as an annual check-up if they resist.",
            "Bring the medication list — some meds mimic dementia and are fixable.",
            "Ask PCP for a cognitive screen (MoCA or SLUMS) and a referral to neurology or a geriatrician.",
        ],
        doctor_questions: [
            "Can we do a cognitive screening today?",
            "What treatable causes should we rule out? (B12, thyroid, sleep apnea, depression, UTI, medications)",
            "Who would you refer us to — neurology, geriatrician, or a memory clinic?",
            "What labs should we run?",
        ],
        documents: [
            "Current medication list (including OTC and supplements)",
            "List of specific examples with dates",
            "Family medical history (dementia, stroke, Parkinson's)",
        ],
        services: [
            s("primary_care", "First stop for most families."),
            s("alz_helpline", "24/7, free, no appointment needed: 1-800-272-3900."),
            s("neurology", "Usually enters via PCP referral after screening."),
            s("geriatrician", "A specialist in older adults — a great alternative to neurology."),
        ],
        service_shifts: {
            enter: ["primary_care", "alz_helpline"],
            fade: [],
        },
        lane_help: [
            "I can help you write down the specific examples you've noticed — what to include, what to leave out.",
            "I can walk you through how to bring this up with your loved one without making them defensive.",
            "I can prep questions for that first PCP visit so nothing important gets missed.",
        ],
    },

    {
        key: "getting_answers",
        color: "#2C7A8C",
        color_soft: "#DFEEF2",
        title: "Getting answers",
        tagline: "PCP visit. Referral. Neurology wait. Tests. Records. Questions.",
        accent: "bg-stage_early text-stage_early-dark",
        accent_dark: "text-stage_early-dark",
        icon: "search",
        fast_label: "Possible FAST 2–3 range",
        matches: (p) => !p?.predx && p?.estimated_stage >= 2 && p?.estimated_stage <= 3 && !p?.has_formal_dx,
        meaning: "A cognitive screen has flagged something. You're in the evaluation window — imaging, labs, second opinions, waiting rooms. This stage feels heavy because you're carrying uncertainty AND daily life.",
        concerns: [
            "\"How long does this workup take, and what are we waiting for?\"",
            "\"Do we need a second opinion? What kind of specialist is right?\"",
            "\"How do I handle the anxiety while we wait?\"",
            "\"What if the diagnosis is Alzheimer's — is there anything we can do?\"",
        ],
        watch_for: [
            "Worsening symptoms during the wait — track them for the specialist",
            "New or sudden confusion — could be a UTI, medication reaction, or delirium (not the same as dementia)",
            "Sleep changes, weight loss, or mood collapse in your loved one",
            "Your own sleep, weight, and mood — this is the stage caregivers first burn out",
        ],
        next_steps: [
            "Ask the workup team for a written plan: what tests, in what order, on what dates.",
            "Consider a memory clinic if wait times are long — they coordinate faster than piecemeal referrals.",
            "Start a Behavior Log now, even without a diagnosis. Data is a gift to whoever picks up the case.",
            "Line up one or two family members you can call after each appointment.",
        ],
        doctor_questions: [
            "What tests are still to come, and how long until we have results?",
            "Which specialists are involved and how do they communicate with each other?",
            "Are we ruling out reversible causes first?",
            "Would you recommend imaging (MRI, PET) or additional cognitive testing?",
            "Do we need blood work for genetic markers or biomarkers?",
        ],
        documents: [
            "Copies of all lab results and imaging reports (yours to keep)",
            "Written referral letters and specialist notes",
            "A running list of every appointment, question, and answer",
        ],
        services: [
            s("neurology", "Primary workup happens here."),
            s("memory_clinic", "Consider if referral wait times are long."),
            s("geriatrician", "Excellent option — treats the whole person."),
            s("social_worker", "Hospital or aging-services social worker helps navigate insurance + referrals."),
            s("support_group", "Even without a diagnosis. Others are here."),
        ],
        service_shifts: {
            enter: ["neurology", "memory_clinic", "social_worker", "support_group"],
            fade: [],
        },
        lane_help: [
            "I can prep a full question list for each specialist visit and organize what you're being told across appointments.",
            "I can explain any lab result, medication side effect, or imaging finding in plain language.",
            "I can be a place to write down the waiting — the fear, the not-knowing — so you don't carry it alone.",
        ],
    },

    {
        key: "diagnosis_next",
        color: "#2D62D9",
        color_soft: "#DFE8FC",
        title: "Diagnosis and next steps",
        tagline: "Understanding the diagnosis, medications, safety, legal + financial planning, family meeting.",
        accent: "bg-stage_middle text-stage_middle-dark",
        accent_dark: "text-stage_middle-dark",
        icon: "flag",
        fast_label: "Often FAST 3–4 range",
        matches: (p) => p?.estimated_stage === 3 || (p?.estimated_stage === 4 && p?.has_formal_dx),
        meaning: "The diagnosis has been given. The first 90 days are for grieving, learning, and putting foundational pieces in place while your loved one can still fully participate. Don't try to do everything at once — pick one thing per week.",
        concerns: [
            "\"How do I even begin to accept this?\"",
            "\"Do we tell people? How much? Who first?\"",
            "\"What do we need to do RIGHT NOW vs. what can wait?\"",
            "\"How much time do we realistically have at the current level?\"",
        ],
        watch_for: [
            "Depression in the newly diagnosed person — very common and treatable",
            "Anosognosia — they may not accept the diagnosis. This is a symptom, not denial.",
            "Family conflict about who does what — surfaces early, better addressed early",
            "Your own shock. Give yourself a real six weeks before making big decisions.",
        ],
        next_steps: [
            "Have the legal conversation — MPOA, DPOA, Living Will, updated Will — while capacity is unquestioned.",
            "Learn about GUIDE (Medicare's dementia care program) — enrollment can happen now.",
            "Start a shared family communication channel so no one is left out or over-informed.",
            "Book a second appointment with the diagnosing physician 30–60 days out to ask everything you forgot.",
        ],
        doctor_questions: [
            "What TYPE of dementia is this (Alzheimer's, vascular, Lewy body, FTD)? The type changes prognosis and treatment.",
            "Are they a candidate for the new anti-amyloid treatments (Leqembi, Kisunla)? What's involved?",
            "How often should we come back?",
            "What symptoms mean 'call us today' vs. 'wait for next visit'?",
        ],
        documents: [
            "Medical Power of Attorney (MPOA / Healthcare Proxy)",
            "Durable Power of Attorney (DPOA) for finances",
            "Living Will / Advance Directive",
            "HIPAA authorization for family members",
            "Updated Will and beneficiaries",
        ],
        services: [
            s("neurology", "Continues — 6-month follow-ups typical."),
            s("geriatrician", "Great option for whole-person care post-diagnosis."),
            s("guide_program", "Enters here — free Medicare-covered navigator + 24/7 line."),
            s("elder_law", "Enters here — one hour covers all four legal docs at once."),
            s("financial_planner", "Consider — long-term care insurance still possible for some early-stage cases."),
        ],
        service_shifts: {
            enter: ["guide_program", "elder_law", "financial_planner"],
            fade: ["memory_clinic"],
        },
        lane_help: [
            "I can help you draft the message you want to send to family, or write it FOR you and let you edit.",
            "I can walk you through what GUIDE is, how to enroll, and which providers near you participate.",
            "I can generate the exact question list for that 30-day follow-up appointment.",
            "I can hold space for the grief and shock. This is a lot. You don't have to be efficient about it.",
        ],
    },

    {
        key: "daily_care",
        color: "#E28800",
        color_soft: "#FDEBD0",
        title: "Daily care changes",
        tagline: "Meals, meds, bathing, sleep, repeat questions, routines, behavior changes.",
        accent: "bg-stage_middle text-stage_middle-dark",
        accent_dark: "text-stage_middle-dark",
        icon: "sun",
        fast_label: "Possible FAST 4–5 range",
        matches: (p) => p?.estimated_stage === 4 || p?.estimated_stage === 5,
        meaning: "Life reorganizes around care. Your loved one needs help with the small daily choices — clothes, meals, timing. They may forget names, mix up family members, or lose track of time. This is when the marathon starts to feel like a marathon.",
        concerns: [
            "\"They don't recognize me sometimes — how do I not take it personally?\"",
            "\"I'm doing everything and no one sees it.\"",
            "\"They're not sleeping — I'm not sleeping.\"",
            "\"When do I stop working? Can we afford for me to?\"",
        ],
        watch_for: [
            "Weight loss — an early red flag. Ask the doctor about it.",
            "Skin breakdown from incontinence — talk to home health early",
            "Falls — even one fall triples the risk of the next",
            "Sundowning — evening agitation. Predictable and manageable with routine changes.",
            "YOUR sleep debt. If you can't remember your last full night, that's medical.",
        ],
        next_steps: [
            "Formalize a daily routine — visible on the wall, same times, same order.",
            "Ask the doctor about sleep meds, sundowning strategies, and appetite support.",
            "Bring in professional help — 4 hours a week grows to 8, grows to 20. Start now.",
            "Tour 2–3 memory care communities proactively even if you never use them.",
            "Take your Sunday burnout check-in every week. It's a vital sign now.",
        ],
        doctor_questions: [
            "Are we on the right medications for behavior + sleep? Any side effects concerning?",
            "How are we managing pain? People with dementia often can't tell us they're hurting.",
            "Weight, blood pressure, and swallowing — how are the numbers trending?",
        ],
        documents: [
            "Updated care plan — who does what, when, on which days",
            "Emergency binder — meds, docs, insurance, doctors, contacts — in ONE folder",
            "Medicaid application (if applicable) — waitlists can be long",
        ],
        services: [
            s("home_health", "Now essential — increase hours as needed."),
            s("adult_day", "Reduces sundowning + gives you back your day."),
            s("driver_eval", "One-time OT-run assessment. Removes the driving fight from the family."),
            s("pharmacist", "Ask for a comprehensive medication review annually."),
            s("guide_program", "24/7 nurse line becomes gold at this stage."),
        ],
        service_shifts: {
            enter: ["home_health", "adult_day", "driver_eval", "pharmacist"],
            fade: [],
        },
        lane_help: [
            "I can name the sundowning pattern and give you three specific things to try tonight.",
            "I can help you decide whether to bring in outside help this week or next.",
            "I can be the witness to how much you're doing. What you're doing is a lot.",
        ],
    },

    {
        key: "care_coordination",
        on_road: false, // support that runs throughout — not a stop on the road
        color: "#7C3AED",
        color_soft: "#EBE2FC",
        title: "Care coordination",
        tagline: "GUIDE, home health, respite, support groups, caregiver wellness, community resources.",
        accent: "bg-lavender/50 text-purple-800",
        accent_dark: "text-purple-800",
        icon: "compass",
        fast_label: "Any FAST stage — runs alongside",
        // Parallel section — always relevant once care is complex.
        // Not marked as primary "you are here" — surfaces alongside
        // whatever primary stage the caregiver is on from Diagnosis
        // forward.
        matches: () => false,
        parallel_from: "diagnosis_next",
        meaning: "This stage runs alongside every other stage from Diagnosis forward. It's the work of stitching together the providers, programs, benefits, and community resources into one coherent plan. Nobody teaches families how to do this — you get to do it anyway.",
        concerns: [
            "\"How do I keep track of who does what?\"",
            "\"How do I keep the medical team, home health, and family on the same page?\"",
            "\"What's actually covered — Medicare, Medicaid, insurance, out-of-pocket?\"",
            "\"Am I doing this right, or missing something obvious?\"",
        ],
        watch_for: [
            "Windows of clarity — take advantage of good days for hard conversations",
            "Life events that change plans — a fall, a hospitalization, a family shift",
            "Coverage gaps between providers (nobody's watching a specific issue)",
            "Support burnout — running the whole care team is a job in itself",
        ],
        next_steps: [
            "Ask the neurologist or PCP to write ONE care summary letter. Share it with every provider.",
            "Enroll in Medicare GUIDE if eligible — it exists to be your quarterback.",
            "Book respite BEFORE you desperately need it. Weekends fill up.",
            "Attend one caregiver support group. Even once. You are not alone in this.",
        ],
        doctor_questions: [
            "Who is the point person on the medical team? Who do I call between visits?",
            "How do you coordinate with the home health team / GUIDE program / social worker?",
            "What decisions might we need to make in the next 6–12 months?",
            "Would you sign a POLST or MOST form with us?",
        ],
        documents: [
            "One-page care summary letter (updated at each visit)",
            "Shared family calendar of appointments",
            "POLST / MOST form (state-specific)",
            "Letter of wishes — non-legal, but often the most important document",
        ],
        services: [
            s("guide_program", "The dementia-specific quarterback role. Free with Medicare."),
            s("home_health", "Continues — often escalates."),
            s("respite", "Non-negotiable. Even one weekend a month."),
            s("adult_day", "Excellent for socialization + your respite."),
            s("elder_law", "Non-negotiable. One hour saves you months of legal grief."),
            s("social_worker", "For Medicaid + benefits navigation."),
            s("support_group", "For you. Weekly, monthly — whatever fits."),
            s("community_meals", "Meals on Wheels or local meal programs."),
            s("transport", "Senior transport or paratransit for appointments."),
        ],
        service_shifts: {
            enter: ["community_meals", "transport", "respite"],
            fade: [],
        },
        lane_help: [
            "I can help you draft the one-page care summary letter your doctors will actually read.",
            "I can walk you through the state-specific docs you need and where to get them.",
            "I can help you evaluate memory care communities without guilt.",
        ],
    },

    {
        key: "transitions",
        on_road: false, // changes can happen at ANY time — not a fixed stop
        color: "#D14D24",
        color_soft: "#FBE3D9",
        title: "Transitions",
        tagline: "Provider changes, navigator turnover, home health discharge, hospice discussion, repeating the story.",
        accent: "bg-stage_middle text-stage_middle-dark",
        accent_dark: "text-stage_middle-dark",
        icon: "arrows",
        fast_label: "Any FAST stage — expect these",
        // Transitions can happen at any stage — usually surfaced by a
        // life event, not by FAST. Not automatically flagged; the
        // caregiver navigates in through the sidebar.
        matches: () => false,
        meaning: "Dementia care is a series of transitions — a new neurologist, a home-health discharge, a hospice conversation, a move to memory care, a family member stepping in or stepping back. Each transition means you tell the story again. Memory Lane is where the story stays put.",
        concerns: [
            "\"Our navigator quit — do we have to start over?\"",
            "\"They're being discharged from home health. Now what?\"",
            "\"The neurologist is retiring. Where do we go?\"",
            "\"How do we keep continuity of care through a hospital stay?\"",
        ],
        watch_for: [
            "Handoff gaps — meds not refilled, follow-ups not scheduled, records not sent",
            "Repeating trauma of retelling the whole story to a new provider",
            "Signs that a current placement isn't working anymore",
            "Family dynamics shifting — someone new stepping in, someone stepping back",
        ],
        next_steps: [
            "Keep a printable care summary current so any new provider can catch up in one page.",
            "When someone leaves the care team, ask them for a written handoff to the next person.",
            "For a home-health discharge, ask WHY, and ask what changes would restart eligibility.",
            "For a placement move (home → memory care), tour proactively and pick before you're desperate.",
        ],
        doctor_questions: [
            "How do you communicate with our other providers?",
            "If you leave this practice, what happens to us?",
            "What triggers a re-evaluation for home health? For hospice?",
            "Would you write a summary letter for a future provider?",
        ],
        documents: [
            "One-page care summary letter (kept current)",
            "Full medication list with dates + prescribers",
            "Recent labs / imaging / discharge summaries",
            "Contact list — every provider, in one place",
        ],
        services: [
            s("guide_program", "The GUIDE navigator role is designed for exactly this — continuity across transitions."),
            s("social_worker", "Hospital + agency social workers often manage handoffs."),
            s("palliative", "May enter here even before hospice — comfort care can start earlier than families realize."),
        ],
        service_shifts: {
            enter: ["palliative"],
            fade: [],
        },
        lane_help: [
            "I can help you write the summary letter, the handoff email, or the letter you'd send to a new provider.",
            "I can hold the whole story so you don't have to retell it every time — new providers can be given a summary.",
            "I can help you compare communities or programs when it's time to move.",
        ],
    },

    {
        key: "advanced_care",
        color: "#C0399F",
        color_soft: "#FADFF2",
        title: "Advanced care needs",
        tagline: "Falls, eating/drinking changes, incontinence, mobility decline, caregiver exhaustion.",
        accent: "bg-stage_late text-stage_late-dark",
        accent_dark: "text-stage_late-dark",
        icon: "shield",
        fast_label: "Possible FAST 6–7 range",
        matches: (p) => p?.estimated_stage === 6,
        meaning: "The body starts to lag behind the mind. Falls happen. Eating gets harder. Mobility declines. Behavior can intensify. You need more hands, better environmental supports, and expert medical review. You are not failing. This is the disease.",
        concerns: [
            "\"They fell — again. What am I supposed to do?\"",
            "\"They keep refusing food / water / meds.\"",
            "\"They won't let me bathe them / dress them / change them.\"",
            "\"Is it time for memory care? How do I know?\"",
        ],
        watch_for: [
            "Any fall — even ONE. Falls are a turning point.",
            "Weight loss + swallowing changes — signal transition. Ask about swallow therapy.",
            "New agitation with a clear trigger — could be a UTI, dehydration, or medication.",
            "Refusal of medication — often means they can no longer swallow safely.",
            "Sudden decline — always warrants a same-week medical check.",
        ],
        next_steps: [
            "Lock or remove firearms, kitchen tools, and car keys — today, not eventually.",
            "Install door alarms, motion sensors, and consider a GPS tracker.",
            "Ask the doctor about behavior meds — used correctly they buy you months of home care.",
            "If you're at your limit, ask about a memory care community. This is not giving up.",
            "Call the Alzheimer's Helpline (1-800-272-3900) tonight if you need to talk to a person.",
        ],
        doctor_questions: [
            "Could this new behavior be a UTI, medication reaction, or pain?",
            "Are there dementia-friendly behavior medications we should consider?",
            "How do we handle refusal of care safely?",
            "What's your honest guidance on home vs. memory care from here?",
            "Do we qualify for palliative or hospice consult now?",
        ],
        documents: [
            "MedicAlert + Alzheimer's Safe Return registration",
            "POLST / MOST form on file with the doctor + hospital + home",
            "Written behavior/emergency plan — what to do at 3am when it's bad",
            "Signed release for the memory care community's assessment (if touring)",
        ],
        services: [
            s("home_health", "24/7 or near-24/7 needed for many families."),
            s("memory_care", "A serious option now. Not a failure. A different kind of care."),
            s("palliative", "Comfort-focused care, still while curative treatment continues."),
            s("ot", "Home-safety modifications and mobility supports."),
            s("speech_therapy", "For swallowing / feeding changes."),
            s("elder_law", "May revisit for Medicaid spend-down."),
        ],
        service_shifts: {
            enter: ["memory_care", "palliative", "ot", "speech_therapy"],
            fade: ["driver_eval", "adult_day"],
        },
        lane_help: [
            "I can name the behavior pattern and give you concrete de-escalation scripts.",
            "I can help you decide if it's a medical event vs. a bad night, and what to do next.",
            "I can help you evaluate memory care communities without guilt.",
            "I can be here at 3am. I don't sleep.",
        ],
    },

    {
        key: "hospice_comfort",
        color: "#5A5AE0",
        color_soft: "#E3E3FB",
        title: "Hospice / comfort care",
        tagline: "Hospice evaluation, goals of care, comfort, family decisions.",
        accent: "bg-stage_late text-stage_late-dark",
        accent_dark: "text-stage_late-dark",
        icon: "moon",
        fast_label: "Often FAST 7",
        matches: (p) => p?.estimated_stage === 7,
        meaning: "Hospice is not giving up. It's a shift from treating the disease to caring for the person. Most families say the same thing after: \"I wish we'd called sooner.\" Comfort, dignity, and presence become the whole plan.",
        concerns: [
            "\"When is the right time to call hospice?\"",
            "\"Will hospice mean no more medical care?\"",
            "\"How do I know they're not in pain?\"",
            "\"How do I be present without falling apart?\"",
        ],
        watch_for: [
            "Swallowing difficulty — sign of transition",
            "Weight loss despite appetite support",
            "Frequent infections — pneumonia, UTIs — that are harder to bounce back from",
            "Long sleep periods, less responsiveness",
            "Your own body starting to hold the goodbye — grief starting early",
        ],
        next_steps: [
            "Ask your doctor for a hospice referral. Hospice can happen at home, in a facility, or in a hospice house.",
            "Meet 1–2 hospice agencies. Choose the one that feels right to YOU.",
            "Set up the comfort kit: familiar music, familiar smells, soft lighting, low visitor volume.",
            "Say what you need to say. Even one sentence. Even if they can't respond.",
            "Ask hospice about respite — you're allowed 5 days off. Take them.",
        ],
        doctor_questions: [
            "Do you agree they qualify for hospice now?",
            "How do we manage pain when they can't tell us?",
            "What signs should I watch for that mean death is close?",
            "Who do I call when something changes?",
            "What happens the day of, and after?",
        ],
        documents: [
            "Hospice election paperwork",
            "DNR / POLST posted visibly in the home",
            "Funeral/burial arrangements finalized",
            "Family notification list — who to call, in what order",
            "Copies of the will, life insurance, and account passwords with the executor",
        ],
        services: [
            s("hospice", "The whole point of this stage."),
            s("palliative", "Continues if not on hospice yet."),
            s("faith_community", "Rituals of goodbye — chaplains, prayers, presence."),
            s("grief_counselor", "Enters HERE, not after. Anticipatory grief is real grief."),
            s("support_group", "Bereavement-specific groups exist even before the loss."),
        ],
        service_shifts: {
            enter: ["hospice", "grief_counselor", "faith_community"],
            fade: ["home_health", "memory_care", "adult_day"],
        },
        lane_help: [
            "I can help you decide if it's hospice time by walking you through the signs.",
            "I can be the place you write the letter, the eulogy, or the thing you couldn't say out loud.",
            "I can sit with you at 3am. That's when it usually hurts.",
        ],
    },

    {
        key: "aftercare",
        color: "#46708F",
        color_soft: "#DEEAF2",
        title: "Grief and family support",
        tagline: "Grief support, records, family wrap-up, preserving the story.",
        accent: "bg-cream-dark text-[#1F2624]",
        accent_dark: "text-[#1F2624]",
        icon: "sunrise",
        fast_label: null, // FAST no longer applies here.
        matches: (p) => p?.bereaved === true,
        meaning: "After they're gone, caregivers often collapse. The role you've had for years — sometimes decades — is over. That leaves a hole. This stage is about honoring what you did, grieving, closing out the practical pieces, and slowly becoming a person who isn't a caregiver.",
        concerns: [
            "\"Who am I now?\"",
            "\"Why can't I stop crying / why can't I cry?\"",
            "\"The paperwork feels endless.\"",
            "\"Everyone's moved on and I'm still here.\"",
        ],
        watch_for: [
            "Sleep collapse — you finally can, and now you can't",
            "Sudden weight loss or gain",
            "Isolation — the visits stop after week 2",
            "Guilt about relief — normal, expected, not a sign of anything wrong",
            "Delayed grief — sometimes it arrives 6–9 months later",
        ],
        next_steps: [
            "Register the death with SSA, VA (if applicable), pension plans, and insurance.",
            "File for the death benefit through SSA if eligible.",
            "Close accounts, transfer titles, cancel subscriptions, update beneficiaries on YOUR accounts.",
            "Join a bereavement group. Most hospices offer 13 months of free support — take all of it.",
            "Book a physical for yourself. Caregiving takes a body toll.",
        ],
        doctor_questions: [
            "How are YOU doing? (Your primary care visit, not theirs.)",
            "Are the physical symptoms I'm having (chest pain, exhaustion, insomnia) grief or something else?",
            "Should I talk to someone — therapist, grief counselor, medication?",
        ],
        documents: [
            "Death certificate — order 10+ copies. You will need them.",
            "Final tax return for the deceased",
            "Estate paperwork with the elder-law attorney or executor",
            "Updated beneficiary forms on YOUR accounts",
            "A memory book / letter file — while the stories are still sharp",
        ],
        services: [
            s("grief_counselor", "Continues from hospice or newly entered here."),
            s("support_group", "Bereavement groups specifically for dementia caregivers."),
            s("faith_community", "Rituals of transition — 30 days, 40 days, one year."),
            s("elder_law", "Final estate closeout."),
        ],
        service_shifts: {
            enter: ["grief_counselor"],
            fade: ["hospice", "palliative", "home_health"],
        },
        lane_help: [
            "I can help you write the obituary, thank-you notes, or the letter you didn't send.",
            "I can walk you through the SSA paperwork, the accounts to close, the calls to make.",
            "I can be here without an agenda. You don't have to be anything today.",
        ],
    },
];

// ---- Alzheimer's / FAST helpers --------------------------------------------

/**
 * Should this profile see FAST staging metadata?
 *
 * FAST is a clinical scale developed for Alzheimer's disease. It works
 * reasonably for AD but does not describe the trajectory of vascular,
 * Lewy body, FTD, or mixed dementias. For everyone whose diagnosis
 * isn't AD (or isn't yet known), we HIDE FAST labels — the plain
 * journey title is the whole product surface.
 *
 * Accepts a patient/intake profile shape with a `dementia_type` field
 * (populated from the patient profile) plus optional legacy hints
 * (`condition`, `diagnosis`). We match "alzheimer" case-insensitively
 * anywhere in those fields.
 */
// The seven JOURNEY AREAS that appear on the road. Transitions and
// Care coordination stay in ROADMAP_STAGES (their educational content
// is still used), but they are not stops: changes can happen at any
// time, and coordination support runs throughout the journey.
export const JOURNEY_AREAS = ROADMAP_STAGES.filter((s) => s.on_road !== false);

// Condensed dashboard timeline — early-stage caregivers see five stages.
// Hospice/comfort care and grief support stay on the FULL roadmap, and are
// progressively disclosed here only once the caregiver has placed their
// marker on (or near) them.
export const LATER_JOURNEY_KEYS = ["hospice_comfort", "aftercare"];

const DASHBOARD_SHORT_TITLES = {
    first_concerns: "First concerns",
    getting_answers: "Getting answers",
    diagnosis_next: "Diagnosis & planning",
    daily_care: "Daily support",
    advanced_care: "Advanced support",
    hospice_comfort: "Comfort care",
    aftercare: "Grief & family support",
};

export function dashboardShortTitle(area) {
    return DASHBOARD_SHORT_TITLES[area.key] || area.title;
}

export function dashboardJourneyAreas(selectedKeys = []) {
    return JOURNEY_AREAS.filter(
        (a) => !LATER_JOURNEY_KEYS.includes(a.key) || selectedKeys.includes(a.key)
    );
}

export function shouldShowFast(profile) {
    if (!profile) return false;
    const hay = [
        profile.dementia_type,
        profile.condition,
        profile.diagnosis,
        profile.dx,
    ]
        .filter(Boolean)
        .map(String)
        .join(" ")
        .toLowerCase();
    if (!hay) return false;
    // "alzheimer", "suspected alzheimer", "possible alzheimer", "AD" (as full word)
    if (hay.includes("alzheimer")) return true;
    if (/\bad\b/.test(hay) && !hay.includes("adhd") && !hay.includes("add ")) return true;
    return false;
}

/**
 * Given a caregiver profile, return the JOURNEY key the caregiver is
 * currently living, OR null if we can't confidently place them.
 *
 * Null triggers the "Let's find where you are" invitation in the UI
 * rather than dumping the caregiver onto "First concerns" by default.
 * The prior version placed everyone at first_concerns which felt
 * presumptuous.
 */
export function currentStageKey(profile) {
    if (!profile) return null;
    // Only auto-place when we have SIGNAL. An empty profile with no
    // intake and no predx = we don't know, and we shouldn't guess.
    const hasSignal = (
        profile.predx === true ||
        profile.bereaved === true ||
        typeof profile.estimated_stage === "number"
    );
    if (!hasSignal) return null;
    for (const stage of ROADMAP_STAGES) {
        if (typeof stage.matches === "function" && stage.matches(profile)) {
            return stage.key;
        }
    }
    return null;
}
